// July 2026 monthly report companion blog.
import { defineDocument, em, link, p, section, strong, text, } from '../../schema';
const julyReportHref = '/party-central/reports/2026-07-top-party-games-and-streamers';
export const blogPartyGameStreamingByTheNumbersJuly2026Document = defineDocument({
    id: 'blog:party-game-streaming-by-the-numbers-july-2026',
    kind: 'blog',
    slug: 'party-game-streaming-by-the-numbers-july-2026',
    route: '/blog/party-game-streaming-by-the-numbers-july-2026',
    seo: {
        title: 'Party Game Streaming by the Numbers: July 2026',
        description: 'Meccha Chameleon accounted for one third of all party game streams in July, but a "Classic" game took the top Party Score this month.',
        imageAsset: 'twitchStats202607',
        imageAlt: 'Collage of top party games and streamers from the July 2026 Twitch data report',
    },
    hero: {
        eyebrow: [text('Airhead Blog')],
        title: [text('Party Game Streaming by the Numbers: July 2026')],
        description: [
            text('Meccha Chameleon accounted for one third of all party game streams in July, but a "Classic" game took the top Party Score this month.'),
        ],
        imageAsset: 'twitchStats202607',
        imageAlt: 'Collage of top party games and streamers from the July 2026 Twitch data report',
        caption: 'Top party games and streamers on Twitch for July 2026, as tracked by the Party Central report.',
        showDescription: true,
        background: 'blurred-image',
    },
    meta: {
        author: 'Jay',
        publishedAt: new Date('2026-08-06T00:00:00.000Z'),
        updatedAt: new Date('2026-08-06T00:00:00.000Z'),
        tags: ['party-central'],
    },
    sections: [
        section({
            id: 'article',
            blocks: [
                p([
                    text('Party game streaming on Twitch had its biggest month since we started counting. We tracked '),
                    strong('269,898 party game streams'),
                    text(' from '),
                    strong('118,110 streamers'),
                    text(' across '),
                    strong('974 games'),
                    text(' and '),
                    strong('35 languages'),
                    text('. That is 52% more streams and 60% more streamers than June, and nearly all of the growth traces back to one game: '),
                    em('MECCHA CHAMELEON'),
                    text('. The '),
                    link(julyReportHref, 'full July 2026 report'),
                    text(' has every ranking and table.'),
                ]),
            ],
            sections: [
                section({
                    id: 'article__cant-catch-chameleon',
                    title: `Can't Catch Chameleon`,
                    blocks: [
                        p([
                            text('In July '),
                            em('MECCHA CHAMELEON'),
                            text(' was streamed more than '),
                            strong('100,000 times'),
                            text(', by more than '),
                            strong('61,000 streamers'),
                            text('. To put that in perspective, that is more streams than the next 16 games combined and seven times more than second-place '),
                            em('Among Us'),
                            text('. It was streamed in at least 35 languages and accounted for more than one in three streams we tracked all month.'),
                        ]),
                        p([
                            text('More than half of all German, Chinese, and Polish party game streams in July were '),
                            em('MECCHA CHAMELEON'),
                            text('. German streams nearly doubled month over month and Chinese streams grew 91%, almost entirely on the back of this hide-and-seek game.'),
                        ]),
                        p([
                            text('After every viral moment eventually comes a cooling off. Despite a drop in daily stream activity of approximately 80% from the beginning of the month to the end of it, no other party game has come close to similar stream numbers.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__party-scores-cut-through-the-noise',
                    title: 'Party Scores Cut Through the Noise',
                    blocks: [
                        p([
                            text('Party Score is not a volume contest. It weighs how many streamers a game reaches, how large their audiences are, and how often they come back to play again.'),
                        ]),
                        p([
                            text('First place went to '),
                            em('Ragnarok Origin: Classic'),
                            text(', which launched in the Americas on July 23 and rode some dedicated Portuguese-language channels to the top spot. Following closely behind, and dropping from the top spot for the first time in months, was '),
                            em('nanos world'),
                            text('. It set yet another "stickiness" high at 15.5 streams per streamer.'),
                        ]),
                        p([
                            em('Riichi City'),
                            text(' was a surprising new entrant to the list, climbing from 64th in June to fifth in July on frequent play from '),
                            link('https://twitch.tv/fer123snake', 'fer123snake'),
                            text(' and three '),
                            link('https://twitch.tv/northernlion', 'Northernlion'),
                            text(' appearances. '),
                            em('Super Battle Golf'),
                            text(' grew its streamer count 49% after a July 16 update and free weekend that added a team mode, a course, and new items. Going the other way, '),
                            em('UNO'),
                            text(' fell from fifth to 13th as its average audience dropped from 22 viewers to 13.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__ponce-edges-a-three-way-tie',
                    title: 'Ponce Edges a Three-Way Tie',
                    blocks: [
                        p([
                            link('https://twitch.tv/ponce', 'Ponce'),
                            text(', '),
                            link('https://twitch.tv/kyr_sp33dy', 'KYR_SP33DY'),
                            text(', and '),
                            link('https://twitch.tv/dooleynoted', 'DooleyNoted'),
                            text(' all finished with a Streamer Party Score of 99. Ponce took first by averaging '),
                            strong('3,451 viewers'),
                            text(' across 14 observations, anchored by '),
                            em('Lycans'),
                            text(' every Tuesday of the month. KYR_SP33DY doubled from four party games in June to eight in July while continuing to average more than 1,400 viewers per stream.'),
                        ]),
                        p([
                            text('For sheer commitment, though, nobody beat '),
                            link('https://twitch.tv/x33n', 'X33N'),
                            text('. We found the channel live with a party game on 30 of the 31 days in July, up from six days in June.'),
                        ]),
                        p([
                            text('Other newcomers to the list included '),
                            link('https://twitch.tv/paache_', 'paache_'),
                            text(', playing games like '),
                            em('Mario Kart World'),
                            text(' and '),
                            em('Wii Sports'),
                            text(', as well as '),
                            link('https://twitch.tv/hcjustin', 'HCJustin'),
                            text(', who cracked the list after adding streams of '),
                            em('PlateUp!'),
                            text(' and '),
                            em('LEGO Party!'),
                            text('.'),
                        ]),
                    ],
                }),
                section({
                    id: 'article__read-the-full-report',
                    title: 'Read the Full Report',
                    blocks: [
                        p([
                            text('There is a lot more in the '),
                            link(julyReportHref, 'full July 2026 report'),
                            text(': complete Party Score rankings, new releases, streamer spotlights, language breakdowns, game deep dives, and streaming patterns by day and hour.'),
                        ]),
                    ],
                }),
            ],
        }),
    ],
});
//# sourceMappingURL=party-game-streaming-by-the-numbers-july-2026.js.map